import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { SignOutEverywhereButton } from './SignOutEverywhereButton';
import { Menu, LogOut, LayoutDashboard, Building2, Users, X, Phone, Landmark, Briefcase, Settings, Activity, ShieldCheck } from 'lucide-react';

const MobileNavbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/companies', label: 'Companies', icon: Briefcase, roles: ['Super Admin'] },
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/vendors', label: 'Vendors & Banks', icon: Building2, roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/banks', label: 'All Banks', icon: Landmark, roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/phone-numbers', label: 'Phone Numbers', icon: Phone, roles: ['Admin', 'Sub Admin', 'Agent', 'Super Admin'] },
    { path: '/users', label: 'User Management', icon: Users, roles: ['Admin', 'Sub Admin', 'Super Admin'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['Admin', 'Super Admin'] },
    { path: '/security-timeline', label: 'Security', icon: ShieldCheck, roles: ['Super Admin'] },
    { path: '/system-health', label: 'System Health', icon: Activity, roles: ['Super Admin'] },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
  };

  return (
    <>
      {/* Mobile Top Bar */}
      <div className="md:hidden sticky top-0 z-50 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-green-500 flex items-center justify-center font-bold text-white shadow-lg">
              BV
            </div>
            <div className="text-white">
              <div className="font-bold text-base">Bank Vendor</div>
              <div className="text-xs text-slate-400">{user?.role}</div>
            </div>
          </div>
          
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-slate-900 text-white border-slate-800">
              <SheetHeader className="border-b border-slate-800 pb-4 mb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-white text-lg">Menu</SheetTitle>
                </div>
                <div className="text-left mt-2">
                  <div className="text-sm text-slate-400">Logged in as</div>
                  <div className="font-semibold text-white">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.role}</div>
                </div>
              </SheetHeader>
              
              <div className="space-y-2">
                {navItems.map((item) => {
                  if (!item.roles.includes(user?.role)) return null;
                  const isActive = location.pathname === item.path;
                  const Icon = item.icon;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              
              <div className="absolute bottom-6 left-4 right-4 space-y-2">
                <SignOutEverywhereButton showLabel className="w-full h-10 border-slate-700 text-white bg-transparent hover:bg-slate-800 hover:text-white" />
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-slate-700 text-white hover:bg-red-600 hover:text-white hover:border-red-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-lg">
        <div className="flex items-center gap-1 py-2 overflow-x-auto no-scrollbar px-2">
          {navItems.map((item) => {
            if (!item.roles.includes(user?.role)) return null;
            const isActive = location.pathname === item.path;
            const Icon = item.icon;

            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex flex-col items-center justify-center gap-1 px-3 py-2 min-w-[64px] flex-shrink-0 transition-colors ${
                  isActive ? 'text-blue-600' : 'text-slate-600'
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : ''}`} />
                <span className={`text-[10px] leading-tight whitespace-nowrap ${isActive ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default MobileNavbar;
